import { DownOutlined } from '@ant-design/icons';
import { faFileExcel } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Dropdown, Input, Space } from 'antd';
import { useCallback, useContext, useMemo } from 'react';

import { FinancialStatisticsComponent } from '@app/components/FinancialStatisticsComponent';
import {
  GET_RECORDS_IMPORT_TEMPLATE_ENDPOINT,
  IMPORT_RECORDS_PATH,
  PREFIX,
  RIGHTS_IS_EXPORT,
  RIGHTS_IS_FINANCIAL_STATISTICS,
  RIGHTS_RECORDS,
  XLSX,
} from '@app/constants';
import { TABLET } from '@app/constants/devicesWidth';
import {
  useAccessRights,
  useNewUserAccessRights,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { HeaderContext } from '@app/providers';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import {
  useExportRecordsByChainToExcelMutation,
  useExportRecordsByOrganizationToExcelMutation,
  useLazyExportRecordsToExcelQuery,
  useLazyExportRecordsToExcelWithoutGroupingQuery,
} from '@app/services/rtkQuery/Records/api';
import type { MultiSelectValue } from '@app/shared/components/MultiSelect/types';
import type { NFiles } from '@app/types';

import { useExcelImport } from '@pages/Clients/ImportFromExcel/hooks/useExcelImport';
import { useRecordsPageHeader } from '@pages/Clients/Records/components/hooks/useRecordsPageHeader';
import type { FormValues } from '@pages/Clients/Records/hooks/useRecords';

import { msToInt32ItemsFilters } from '@pages/Clients/Records/utils/msToInt32ItemsFilters';

import type { MenuProps } from 'antd';
import type { Dayjs } from 'dayjs';

import '@app/pages/Clients/Records/styles/RecordsPageHeader.desktop.scss';

export const RecordsPageHeader = () => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();
  const { innerWidth } = useWindowSize();
  const [exportRecordsByChainToExcel] =
    useExportRecordsByChainToExcelMutation();
  const [exportRecordsByOrganizationToExcel] =
    useExportRecordsByOrganizationToExcelMutation();
  const [exportRecordsToExcelWithoutGrouping] =
    useLazyExportRecordsToExcelWithoutGroupingQuery();
  const [exportRecordsToExcel] = useLazyExportRecordsToExcelQuery();
  const { hasUserRight: hasUserRightToExport } = useAccessRights(
    RIGHTS_RECORDS,
    RIGHTS_IS_EXPORT
  );
  const { hasUserRight: hasUserRightToFinancialStatistics } = useAccessRights(
    RIGHTS_RECORDS,
    RIGHTS_IS_FINANCIAL_STATISTICS
  );

  const {
    importInputRef,
    handleExportExcelTemplateClick,
    handleImportExcelClick,
    updateFileByExcel,
  } = useExcelImport({
    templateEndpoint: GET_RECORDS_IMPORT_TEMPLATE_ENDPOINT,
    importPath: IMPORT_RECORDS_PATH,
  });

  const {
    userAccessRights: {
      IsAdmin,
      IsSuperAdmin,
      Records: { IsCreate: hasUserRightToCreate },
    },
  } = useNewUserAccessRights();

  const isOwner = useMemo(
    () => IsAdmin || IsSuperAdmin,
    [IsAdmin, IsSuperAdmin]
  );

  const isMobile = innerWidth <= TABLET;
  const { header } = useContext(HeaderContext);

  const { financialStatistics } = useRecordsPageHeader();

  const getIsoDate = (date: [Dayjs | null, Dayjs | null] | null) => {
    if (!date) return [null, null];
    const [start, end] = date;
    return [
      start && start.isValid()
        ? start.startOf('day').format('YYYY-MM-DD')
        : null,
      end && end.isValid() ? end.endOf('day').format('YYYY-MM-DD') : null,
    ];
  };

  const handleFileData = useCallback(
    (file: NFiles.ModelFile) => {
      if (!file)
        return showNotification(
          'При получении файла произошла ошибка',
          'frontWarning'
        );

      const clientServicesPage = document.querySelector('.bc-export-block');
      const downloadButton = document.createElement('a');
      downloadButton.setAttribute('href', `data:${XLSX};base64,${file.Data}`);

      downloadButton.setAttribute('download', file?.Name || 'Без имени');
      downloadButton.setAttribute('style', 'display: hidden');
      clientServicesPage?.appendChild(downloadButton);
      downloadButton.click();
    },
    [showNotification]
  );

  type ExportRecordsByChainToExcelFn =
    | typeof exportRecordsByChainToExcel
    | typeof exportRecordsByOrganizationToExcel;

  const exportRecordsHandler = useCallback(
    async (apiMethod: ExportRecordsByChainToExcelFn) => {
      const formValues = header?.formValues as FormValues;
      const recordTagsMS = header?.recordTagsMS as MultiSelectValue;
      const clientTagsMS = header?.clientTagsMS as MultiSelectValue;

      if (!formValues) {
        showNotification('Неверные данные для экспорта', 'warning');
        return;
      }

      const [DateCreateS, DateCreateE] = getIsoDate(formValues?.DateCreate);
      const [DateVisitS, DateVisitE] = getIsoDate(formValues?.DateVisit);

      const RecordTagsFilters = msToInt32ItemsFilters(recordTagsMS);
      const ClientTagsFilters = msToInt32ItemsFilters(clientTagsMS);
      const Statuses = formValues.selectedStatuses?.filter(
        (item) => item !== undefined && item !== null
      );

      const request = {
        DateCreateS,
        DateCreateE,
        DateVisitS,
        DateVisitE,
        RecordTagsFilters,
        ClientTagsFilters,
        Statuses,
      };

      try {
        const response = await apiMethod({
          ActiveBranchId,
          data: request,
        }).unwrap();

        handleFileData(response);
      } catch (error) {
        showNotification(error, 'warning');
      }
    },
    [showNotification, ActiveBranchId, handleFileData, header]
  );

  type ExportRecordsToExcelFn =
    | typeof exportRecordsToExcel
    | typeof exportRecordsToExcelWithoutGrouping;

  const exportRecordsWithoutFiltersHandler = async (
    apiMethod: ExportRecordsToExcelFn
  ) => {
    if (!header.searchId) {
      showNotification('Неверные данные для экспорта', 'warning');
      return;
    }

    try {
      const response = await apiMethod({
        ActiveBranchId,
        searchId: header.searchId as number,
      }).unwrap();

      handleFileData(response);
    } catch (error) {
      showNotification(error, 'warning');
    }
  };

  // Обработчик вешаем на сам пункт меню (`onClick` элемента), а не на внутренний
  // `<span>` с текстом — иначе кликабельна только надпись, а не вся строка
  // (issue/11090).
  const items: MenuProps['items'] = [
    {
      key: '1',
      label: 'Записи',
      onClick: () => exportRecordsWithoutFiltersHandler(exportRecordsToExcel),
    },
    {
      key: '2',
      label: 'Услуги записей',
      onClick: () =>
        exportRecordsWithoutFiltersHandler(exportRecordsToExcelWithoutGrouping),
    },
    ...(isOwner
      ? [
          {
            key: '3',
            label: (
              <span data-testid="export-network-records">Записи сети</span>
            ),
            onClick: () => exportRecordsHandler(exportRecordsByChainToExcel),
          },
        ]
      : []),
    ...(isOwner
      ? [
          {
            key: '4',
            label: (
              <span data-testid="export-organization-records">
                Записи организации
              </span>
            ),
            onClick: () =>
              exportRecordsHandler(exportRecordsByOrganizationToExcel),
          },
        ]
      : []),
    ...(hasUserRightToCreate
      ? [
          {
            key: '5',
            label: (
              <span
                className="unselectable"
                data-testid="export-records-template"
              >
                Выгрузить шаблон создания записей
              </span>
            ),
            onClick: () => handleExportExcelTemplateClick(),
          },
        ]
      : []),
    ...(hasUserRightToCreate
      ? [
          {
            key: '6',
            label: (
              <div className="unselectable" data-testid="create-records-excel">
                <span>Создание записей через Excel</span>
                <Input
                  ref={importInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  accept=".xlsx"
                  onChange={(e) => {
                    updateFileByExcel(e);
                  }}
                />
              </div>
            ),
            onClick: () => handleImportExcelClick(),
          },
        ]
      : []),
  ];

  return (
    <div className={`${PREFIX}records-page-header`}>
      <Space direction="horizontal">
        {hasUserRightToFinancialStatistics && (
          <FinancialStatisticsComponent
            financialStatistics={financialStatistics}
            dateString={''}
            isCalendarComponent={false}
            dropdownButtonDataTestId="completed-sum-value"
          />
        )}
        {hasUserRightToExport && (
          // Меню открывается в правом верхнем углу — там же, где всплывают
          // тост-уведомления (`.ant-notification`, z-index 2050). Без подъёма
          // z-index тост перекрывает верхние пункты меню («Записи»), и клик по
          // ним не доходит до обработчика (issue/11090).
          <Dropdown menu={{ items }} overlayStyle={{ zIndex: 2060 }}>
            <Button data-testid="export-to-excel">
              <Space>
                {isMobile ? (
                  <FontAwesomeIcon icon={faFileExcel} size={'lg'} />
                ) : (
                  <>
                    Выгрузить в Excel
                    <DownOutlined />
                  </>
                )}
              </Space>
            </Button>
          </Dropdown>
        )}
      </Space>
    </div>
  );
};
